/**
 * Thin fetch wrapper around the admin API.
 * - Reads base URL from VITE_API_BASE (set in .env)
 * - Attaches Bearer header when a token is passed
 * - Throws ApiError on non-2xx or { ok: false } responses
 */

const API_BASE = import.meta.env.VITE_API_BASE || 'https://dev.lobbyplay.app/api/admin'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

type ApiOptions = Omit<RequestInit, 'body'> & {
  token?: string
  body?: unknown
}

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { token, body, headers, ...rest } = options
  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string>),
  }
  if (token) finalHeaders['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  // Tolerate empty body / non-JSON responses
  const text = await res.text()
  const data = text ? safeParse(text) : {}

  if (!res.ok || (data && data.ok === false)) {
    throw new ApiError(res.status, (data && data.error) || `HTTP ${res.status}`)
  }
  return data as T
}

function safeParse(s: string): any {
  try { return JSON.parse(s) } catch { return {} }
}
